import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { CalendarIcon, CheckCircle2, Download, Calendar as CalendarLucide } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { generateICS, downloadICS, generateGoogleCalendarURL, generateOutlookCalendarURL, getTimeSlotDisplay, getTimeSlotStartTime, getTimeSlotEndTime } from "@/utils/calendarGenerator";
import { Card } from "@/components/ui/card";

const bookingFormSchema = z.object({
  customer_name: z.string().min(2, "Name must be at least 2 characters"),
  customer_email: z.string().email("Invalid email address"),
  customer_phone: z.string().min(10, "Phone number must be at least 10 digits"),
  service_id: z.string().min(1, "Please select a service"),
  preferred_date: z.date({
    required_error: "Please select a date",
  }),
  preferred_time_slot: z.enum(["morning", "afternoon", "evening"], {
    required_error: "Please select a time slot",
  }),
  address: z.string().min(5, "Address is required"),
  city: z.string().min(2, "City is required"),
  zip_code: z.string().min(5, "Zip code is required"),
  appointment_type: z.enum(["estimate", "inspection", "repair", "consultation"]),
  notes: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingFormSchema>;

interface BookingFormProps {
  services: Array<{ id: string; name: string }>;
  cities: string[];
}

export function BookingForm({ services, cities }: BookingFormProps) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [appointmentId, setAppointmentId] = useState<string | null>(null);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      appointment_type: "estimate",
      notes: "",
    },
  });

  const watchedDate = form.watch("preferred_date");
  const watchedTimeSlot = form.watch("preferred_time_slot");

  const onSubmit = async (values: BookingFormValues) => {
    setIsSubmitting(true);
    try {
      // Enhance notes with company info and resources
      const enhancedNotes = [
        values.notes,
        values.notes ? "\n---\n" : "",
        `Customer Resources: https://quote.safewayroofingandchimney.com/welcome`,
        `Booking Date: ${format(new Date(), "PPP 'at' p")}`,
        `To reschedule: https://quote.safewayroofingandchimney.com/book-appointment`,
      ].filter(Boolean).join("\n");

      const appointmentData = {
        customer_name: values.customer_name,
        customer_email: values.customer_email,
        customer_phone: values.customer_phone,
        service_id: values.service_id,
        preferred_date: format(values.preferred_date, "yyyy-MM-dd"),
        preferred_time_slot: values.preferred_time_slot,
        address: values.address,
        city: values.city,
        zip_code: values.zip_code,
        appointment_type: values.appointment_type,
        notes: enhancedNotes,
        status: "pending",
      };

      console.log("📅 Submitting appointment data:", appointmentData);

      const { data, error } = await supabase
        .from("appointments")
        .insert(appointmentData)
        .select()
        .single();

      if (error) {
        console.error("❌ Supabase error details:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      console.log("✅ Appointment created successfully:", data);
      setAppointmentId(data.id);
      setBookingComplete(true);
      toast.success("Appointment booked successfully!");
    } catch (error: any) {
      console.error("❌ Booking error:", error);
      console.error("Error details:", {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
        stack: error.stack,
      });
      toast.error(`Failed to book appointment: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadCalendar = () => {
    if (!appointmentId || !watchedDate || !watchedTimeSlot) return;

    const serviceName = services.find(s => s.id === form.getValues("service_id"))?.name || "Service";
    const startTime = getTimeSlotStartTime(watchedDate, watchedTimeSlot);
    const endTime = getTimeSlotEndTime(watchedDate, watchedTimeSlot);

    const customerNotes = form.getValues("notes");
    const description = [
      `${form.getValues("appointment_type")} appointment for ${serviceName}`,
      customerNotes ? `\n\nCustomer Notes:\n${customerNotes}` : "",
      "\n\n---\nTo learn more about Safeway Roofing and our services, visit:",
      "https://quote.safewayroofingandchimney.com/welcome",
      "\n\nNeed to reschedule?",
      "Visit: https://quote.safewayroofingandchimney.com/book-appointment",
    ].filter(Boolean).join("");

    const icsBlob = generateICS({
      id: appointmentId,
      title: `Safeway Roofing - ${serviceName}`,
      description,
      location: `${form.getValues("address")}, ${form.getValues("city")}, NY ${form.getValues("zip_code")}`,
      startDate: startTime,
      endDate: endTime,
      status: "TENTATIVE",
    });

    downloadICS(icsBlob, `safeway-roofing-appointment-${appointmentId}.ics`);
    toast.success("Calendar event downloaded!");
  };

  if (bookingComplete && appointmentId && watchedDate && watchedTimeSlot) {
    const serviceName = services.find(s => s.id === form.getValues("service_id"))?.name || "Service";
    const startTime = getTimeSlotStartTime(watchedDate, watchedTimeSlot);
    const endTime = getTimeSlotEndTime(watchedDate, watchedTimeSlot);

    const customerNotes = form.getValues("notes");
    const description = [
      `${form.getValues("appointment_type")} appointment for ${serviceName}`,
      customerNotes ? `\n\nCustomer Notes:\n${customerNotes}` : "",
      "\n\n---\nTo learn more about Safeway Roofing and our services, visit:",
      "https://quote.safewayroofingandchimney.com/welcome",
      "\n\nNeed to reschedule?",
      "Visit: https://quote.safewayroofingandchimney.com/book-appointment",
    ].filter(Boolean).join("");

    const calendarEvent = {
      id: appointmentId,
      title: `Safeway Roofing - ${serviceName}`,
      description,
      location: `${form.getValues("address")}, ${form.getValues("city")}, NY ${form.getValues("zip_code")}`,
      startDate: startTime,
      endDate: endTime,
      status: 'TENTATIVE' as const,
    };

    const googleCalendarURL = generateGoogleCalendarURL(calendarEvent);
    const outlookCalendarURL = generateOutlookCalendarURL(calendarEvent);

    return (
      <div className="text-center py-8">
        <div className="flex justify-center mb-6">
          <div className="bg-green-100 dark:bg-green-900/20 p-4 rounded-full">
            <CheckCircle2 className="w-16 h-16 text-green-600 dark:text-green-400" />
          </div>
        </div>
        
        <h2 className="text-2xl font-bold mb-2">Appointment Booked!</h2>
        <p className="text-muted-foreground mb-8">
          We've received your appointment request and will contact you shortly to confirm.
        </p>

        <Card className="p-6 mb-6 text-left">
          <h3 className="font-semibold mb-4 text-lg">Appointment Details</h3>
          <div className="space-y-3">
            <div>
              <span className="font-medium">Service:</span>
              <p className="text-muted-foreground">{serviceName}</p>
            </div>
            <div>
              <span className="font-medium">Type:</span>
              <p className="text-muted-foreground capitalize">{form.getValues("appointment_type")}</p>
            </div>
            <div>
              <span className="font-medium">Preferred Date:</span>
              <p className="text-muted-foreground">
                {format(watchedDate, "EEEE, MMMM d, yyyy")}
              </p>
            </div>
            <div>
              <span className="font-medium">Time:</span>
              <p className="text-muted-foreground">{getTimeSlotDisplay(watchedTimeSlot)}</p>
            </div>
            <div>
              <span className="font-medium">Location:</span>
              <p className="text-muted-foreground">
                {form.getValues("address")}, {form.getValues("city")}, NY {form.getValues("zip_code")}
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-3">
          <p className="text-sm font-medium text-muted-foreground mb-2">Add to your calendar:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button 
              onClick={() => window.open(googleCalendarURL, '_blank')}
              variant="outline"
              className="w-full"
            >
              <CalendarLucide className="mr-2 h-4 w-4" />
              Google Calendar
            </Button>
            
            <Button 
              onClick={() => window.open(outlookCalendarURL, '_blank')}
              variant="outline"
              className="w-full"
            >
              <CalendarLucide className="mr-2 h-4 w-4" />
              Outlook
            </Button>
            
            <Button 
              onClick={handleDownloadCalendar}
              variant="outline"
              className="w-full"
            >
              <Download className="mr-2 h-4 w-4" />
              Apple Calendar
            </Button>
            
            <Button 
              onClick={handleDownloadCalendar}
              variant="outline"
              className="w-full"
            >
              <Download className="mr-2 h-4 w-4" />
              Download ICS
            </Button>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t space-y-4">
          <Card className="p-4 bg-muted/50">
            <h4 className="font-semibold mb-2">Learn More About Us</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Visit our welcome page to learn more about Safeway Roofing and our company.
            </p>
            <Button 
              onClick={() => window.open('https://quote.safewayroofingandchimney.com/welcome', '_blank')}
              variant="default"
              className="w-full"
            >
              Visit Our Website
            </Button>
          </Card>

          <Card className="p-4 bg-muted/50">
            <h4 className="font-semibold mb-2">Need to Reschedule?</h4>
            <p className="text-sm text-muted-foreground mb-3">
              If you need to change your appointment time, you can book a new appointment.
            </p>
            <Button 
              onClick={() => window.location.href = '/book-appointment'}
              variant="outline"
              className="w-full"
            >
              Reschedule Appointment
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Progress Indicator */}
        <div className="flex justify-between mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors",
                  step >= s
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {s}
              </div>
              {s < 4 && (
                <div
                  className={cn(
                    "flex-1 h-1 mx-2 transition-colors",
                    step > s ? "bg-primary" : "bg-muted"
                  )}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Select Service</h3>
            <FormField
              control={form.control}
              name="service_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="appointment_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Appointment Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="estimate">Free Estimate</SelectItem>
                      <SelectItem value="inspection">Inspection</SelectItem>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="consultation">Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Step 2: Contact Information */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Contact Information</h3>
            <FormField
              control={form.control}
              name="customer_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Smith" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customer_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customer_phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Step 3: Location & Date */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Location & Schedule</h3>
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select city" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="zip_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zip Code</FormLabel>
                    <FormControl>
                      <Input placeholder="11733" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="preferred_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Preferred Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date() || date.getDay() === 0
                        }
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="preferred_time_slot"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Time</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8 AM - 12 PM)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12 PM - 4 PM)</SelectItem>
                      <SelectItem value="evening">Evening (4 PM - 7 PM)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Step 4: Additional Details */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Additional Details</h3>
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Special Requests or Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any additional information you'd like us to know..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="p-4 bg-muted rounded-lg space-y-2">
              <h4 className="font-semibold">Appointment Summary</h4>
              <p className="text-sm text-muted-foreground">
                <strong>Service:</strong> {services.find(s => s.id === form.watch("service_id"))?.name}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Type:</strong> {form.watch("appointment_type")}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Date:</strong> {watchedDate && format(watchedDate, "MMMM d, yyyy")}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Time:</strong> {watchedTimeSlot && getTimeSlotDisplay(watchedTimeSlot)}
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Location:</strong> {form.watch("address")}, {form.watch("city")}, NY {form.watch("zip_code")}
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={isSubmitting}
            >
              Back
            </Button>
          )}
          {step < 4 ? (
            <Button
              type="button"
              onClick={async () => {
                const fields = {
                  1: ["service_id", "appointment_type"],
                  2: ["customer_name", "customer_email", "customer_phone"],
                  3: ["address", "city", "zip_code", "preferred_date", "preferred_time_slot"],
                }[step] as Array<keyof BookingFormValues>;

                const isValid = await form.trigger(fields);
                if (isValid) setStep(step + 1);
              }}
              className="ml-auto"
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isSubmitting}
              className="ml-auto"
            >
              {isSubmitting ? "Booking..." : "Confirm Appointment"}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
